<script>
  import SearchBar from './SearchBar.vue';
  import AreaSelectorMenu from '../../view/components/area/components/AreaSelectorMenu.vue';
  import { areaGetRequest } from '../../requests/areaRequest';
  import { parseAreaFromRaw } from '../../parser/areaParser';

  export default {
    props: {
      activeBranchId: Number,
      preferredBranchId: Number,
      showMasterButton: {
        type: Boolean,
        default: false,
      },
      userId: Number,
      isGroupLevelDashboard: Boolean,
      isGroupOwner: {
        type: Boolean,
        default: false,
      },
      page: {
        type: String,
        default: "",
      },
      initialAreas: {
        type: Array,
        default: null,
      },
      hasPro: {
        type: Boolean,
        default: false,
      },
      hasTrial: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        showList: false,
        employmentMap: {},
        employmentList: [],
        searchInput: "",
        areaRootIds: [],
        areaMap: new Map(),
        areaChildIds: new Set(),
        hasLoadedAreas: false,
        areaSearchCache: {},
        branchSearchCache: {},
      }
    },
    mounted() {
      if (this.initialAreas) {
        this.setAreas(this.initialAreas);
        this.hasLoadedAreas = true;
      } else if (!this.isGroupOwner) {
        // Non-owners cannot load areas, so keep the plain branch list available.
        this.hasLoadedAreas = true;
      }
      this.syncDropdownActiveState(this.showList);
    },
    beforeUnmount() {
      this.syncDropdownActiveState(false);
    },
    watch: {
      showList(isActive) {
        this.syncDropdownActiveState(isActive);
      },
    },
    methods: {
      syncDropdownActiveState(isActive) {
        const dropdown = document.querySelector("#company-dropdown");
        const button = document.querySelector("#company-dropdown-btn");

        dropdown?.classList.toggle("active", isActive);
        button?.classList.toggle("active", isActive);
      },
      initData(employments) {
        this.employmentMap = {};
        this.employmentList = employments;
        this.employmentList.forEach(branch => {
          this.employmentMap[branch.id] = branch;
        });
        this.rebuildAreaChildIds();
      },
      selectBranch(id) {
        if (this.isGroupLevelDashboard || this.page === "area-summary") {
          // go to selected branch's summary page
          window.location.href = `/dashboard/${this.userId}/summary?c=${id}`;
          return;
        }
        // UserSelectCompany is a global function from dashboard_base.html
        return UserSelectCompany(id);
      },
      async showBranchList() {
        if (this.showList === true) {
          return;
        }

        this.showList = true;

        if (this.isGroupOwner && (!this.hasLoadedAreas || this.page === "area")) {
          this.clearAreas();
          await this.getAreas();
          this.hasLoadedAreas = true;
        }
      },
      closeBranchList() {
        this.showList = false;
      },
      updateBranchPos(newEmploymentOrder) {
        this.initData(newEmploymentOrder);
      },
      isActive(branch) {
        return branch.id == this.activeBranchId
      },
      goToGroupLevelDashboard() {
        window.location.href = `/dashboard/${this.userId}/iam?c=${this.activeBranchId}`;
      },
      handleEmitSearchOnInput(input) {
        this.searchInput = input.trim().toLowerCase();
        this.areaSearchCache = {};
        this.branchSearchCache = {};
      },
      async getAreas() {
        const result = await areaGetRequest(this.userId, this.activeBranchId, { maxDepth: 10 });
        if (!result?.areas) {
          return;
        }

        this.setAreas(result.areas);
      },
      setAreas(areas) {
        for (const area of areas) {
          const parsedItems = parseAreaFromRaw(area);

          for (const areaItem of parsedItems) {
            if (areaItem.parentId === 0) {
              this.areaRootIds.push(areaItem.id);
            }

            this.areaMap.set(areaItem.id, areaItem);
          }
        }
        this.rebuildAreaChildIds();
      },
      rebuildAreaChildIds() {
        const nextAreaChildIds = new Set();
        for (const areaItem of this.areaMap.values()) {
          areaItem.branchIds.forEach(branchId => {
            nextAreaChildIds.add(branchId);
          });
        }
        this.areaChildIds = nextAreaChildIds;
      },
      clearAreas() {
        this.areaRootIds = [];
        this.areaMap = new Map();
        this.areaChildIds = new Set();
        this.areaSearchCache = {};
        this.branchSearchCache = {};
      },
      branchMatchesSearch(id) {
        if (this.searchInput === "") {
          return true;
        }

        if (this.branchSearchCache[id] !== undefined) {
          return this.branchSearchCache[id];
        }

        const b = this.employmentMap[id];
        const match = b && b.name.toLowerCase().includes(this.searchInput) || false;

        this.branchSearchCache[id] = match;
        return match;
      },
      areaMatchesSearch(id) {
        if (this.searchInput === "") {
          return true;
        }

        if (this.areaSearchCache[id] !== undefined) {
          return this.areaSearchCache[id];
        }

        const area = this.areaMap.get(id);
        if (!area) {
          this.areaSearchCache[id] = false;
          return false;
        }

        let match = false;
        if (area.name?.toLowerCase().includes(this.searchInput)) {
          match = true;
        } else if (area.subareaIds && area.subareaIds.some(subId => this.areaMatchesSearch(subId))) {
          match = true;
        } else if (area.branchIds && area.branchIds.some(branchId => this.branchMatchesSearch(branchId))) {
          match = true;
        }
  
        this.areaSearchCache[id] = match;
        return match;
      },
      isBranchDisabled(branch) {
        if (this.hasPro || this.hasTrial) {
          return false;
        }

        const defaultBranch = this.employmentList?.[0];
        if (!defaultBranch) {
          return false;
        }

        return branch.id !== defaultBranch.id;
      },
    },
    computed: {
      activeBranch() {
        return this.employmentMap?.[this.activeBranchId];
      },
      preferredBranch() {
        if (this.isGroupOwner && !this.hasLoadedAreas) {
          return null;
        }
        const branch = this.employmentMap?.[this.preferredBranchId];
        if (!branch || this.areaChildIds.has(branch.id)) {
          return null;
        }
        return branch;
      },
      employmentsComputed() {
        if (this.isGroupOwner && !this.hasLoadedAreas) {
          return [];
        }
        return this.employmentList.filter(b => {
          const nameHasSearchInput = b.name.toLowerCase().includes(this.searchInput.toLowerCase());
          return !b.isPreferred && nameHasSearchInput && !this.areaChildIds.has(b.id);
        });
      },
      masterButtonClass() {
        return this.isGroupLevelDashboard ? "selected" : "";
      },
    },
    components: {
      SearchBar,
      AreaSelectorMenu,
    },
    provide() {
      return {
        branchSelectorMenu: {
          selectBranch: this.selectBranch,
          isActive: this.isActive,
          areaMatchesSearch: this.areaMatchesSearch,
          branchMatchesSearch: this.branchMatchesSearch,
          areaUrl: (areaId) => `/dashboard/${this.userId}/summary?area=${areaId}`,
          isBranchDisabled: this.isBranchDisabled,
        },
      };
    },
  }
</script>
<template>
  <ul v-show="showList" class="branch-list-menu">
    <SearchBar
      :param-input="searchInput"
      :is-search-bar-open="true"
      :placeholder-text="$t('branch_menu.search_placeholder')"
      :search-on-input="true"
      @emit-search-on-input="handleEmitSearchOnInput"
      class="mb-2"
    />

    <li v-if="showMasterButton" class="branch-item mt-0" :class="masterButtonClass">
      <a class="branch-link" @click="goToGroupLevelDashboard">
        <div class="flex-centered flex-gap">
          <svg class="svg-header-dimension">
            <use xlink:href="/static/dashboard/oplus_custom_icons-1.svg#switch-branch-icon"/>
          </svg>
          {{ $t('dash.nav.master') }}
        </div>
      </a>
    </li>

    <div class="divider text-center" :data-content="$t('branch_menu.selected_branch')"></div>
    <li v-if="activeBranch" class="branch-item selected">
      <a class="branch-link">{{ activeBranch.name }}</a>
    </li>

    <div class="mt-2">
      <AreaSelectorMenu
        v-for="(id, index) in areaRootIds"
        :key="id"
        :area-id="id"
        :depth="0"
        :is-last="index === areaRootIds.length - 1"
        :search-input="searchInput"
        :employment-map="employmentMap"
        :area-map="areaMap"
      />
    </div>

    <div class="divider"></div>
    <li
      v-if="preferredBranch"
      class="branch-item"
      :class="{'disabled-btn': isBranchDisabled(preferredBranch)}"
    >
      <a class="branch-link" @click="selectBranch(preferredBranch.id);">{{ preferredBranch.name }}
        <i class="preferred-branch-icon zmdi zmdi-pin-assistant tooltip tooltip-left" :data-tooltip="$t('user.preferred_branch')"></i>
      </a>
    </li>
    <li v-for="branch in employmentsComputed"
      :key="branch.id" class="branch-item"
      :class="{'is-active': isActive(branch), 'disabled-btn': isBranchDisabled(branch)}"
    >
      <a class="branch-link" @click="selectBranch(branch.id);">{{ branch.name }}</a>
    </li>
  </ul>
</template>
<style scoped>
  .branch-list-menu {
    position: absolute;
    min-width: 120px;
    max-width: 310px;
    max-height: 50vh;
    width: fit-content;
    white-space: nowrap;
    overflow-y: auto;
    overflow-x: hidden;
    background: #fff;
    border-radius: 5px;
    box-shadow: 0 0.05rem 0.2rem rgba(48,55,66,.3);
    list-style: none;
    margin: 0;
    padding: 0.4rem;
    transform: translateY(0.2rem);
    z-index: 300;
    animation: slide-down .15s ease 1;
    color: #5F6B71;
  }

  @keyframes slide-down {
    0% {
      opacity: 0;
      transform: translateY(-1.6rem);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Override spectre's a tag design */
  a.branch-link {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: inherit;
    border-radius: 0.1rem;
    color: inherit;
    display: block;
    margin: 0 -0.4rem;
    padding: 0.2rem 0.8rem 0.2rem 0.4rem;
    text-decoration: none;
  }

  .branch-item {
    padding: 0 0.4rem;
    text-decoration: none;
    user-select: none;
    position: relative;
  }

  .branch-link:hover {
    color: #52a3d5;
    border-radius: 5px;
    background: #f1f1fc;
  }

  .branch-item.selected {
    background: #f1f1fc;
    color: #52a3d5;
    border-radius: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .branch-item.is-active {
    color: #52a3d5;
  }

  .branch-item + .branch-item {
    margin-top: 0.2rem;
  }

  .branch-list-menu::-webkit-scrollbar {
    width: 10px;
  }

  .branch-list-menu::-webkit-scrollbar-track {
    border-radius: 0 5px 5px 0;
    border: 0.1px solid #e6e6e6;
  }

  .branch-list-menu::-webkit-scrollbar-thumb {
    background-color: #62799082;
    border: 3px solid transparent;
    border-radius: 10px;
    background-clip: content-box;
    cursor: pointer;
  }

  .branch-list-menu .zmdi-lock {
    margin-left: 8px;
    color: #4897c9;
  }

  @media (max-width: 360px) {
    .branch-list-menu {
      max-width: 270px;
    }
  }

  .preferred-branch-icon {
    position: absolute;
    right: 2%;
    bottom: 50%;
    transform: translateY(50%);
  }
</style>
